import React, { FC, useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Avatar, Colors, FlexBox } from '@brg/ui';
import { Designer, DesignerAPI, API } from '@brg/api';
import { computeDesigner } from 'shared/helpers/computeData';
import { SearchInputComponent } from 'shared/components/SearchInput/SearchInputComponent';
import { useTheme } from 'styled-components';

export type AuthorT = Designer<'Local'> & { disabled?: boolean };

const FieldContainer = styled(FlexBox)`
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.label};
  font-weight: 500;
`;



const DropdownContainer = styled.div`
  position: absolute;
  z-index: 1000;
  top: 100%;
  background-color: ${({ theme }) => theme.projectEdit.inputs.bg};
  max-height: 200px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 6px 6px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
  margin-bottom: 50px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.mobileSearch.scroll.background};
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.mobileSearch.scroll.body};
    border-radius: 2px;
    width: 4px;
  }
`;

const DropdownItem = styled(FlexBox)`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid ${Colors.galBlack10};
  
  &:hover {
    background-color: ${Colors.galBlack5};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const AuthorName = styled.span`
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.inputs.color};
`;

const AuthorDisplay = styled(FlexBox)`
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  border-radius: 4px;
  align-items: center;
  gap: 8px;
`;

const AuthorChip = styled(FlexBox)`
  background-color: ${Colors.galBlack5};
  border: 1px solid ${Colors.galBlack21};
  border-radius: 4px;
  padding: 4px 8px;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.projectEdit.inputs.color};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  color: ${Colors.galBlack47};
  font-size: 12px;
  
  &:hover {
    background-color: ${Colors.galBlack10};
  }
`;

interface AuthorFieldProps {
  value: AuthorT[];
  onChange: (authors: AuthorT[]) => void;
  required?: boolean;
  placeholder?: string;
}

const AuthorField: FC<AuthorFieldProps> = ({
  value,
  onChange,
  required = false,
  placeholder
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
  const [searchText, setSearchText] = useState('');
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Поиск дизайнеров
      useEffect(() => {
      if (searchText.length >= 2) {
        console.log('Searching for designers with:', searchText);
        setIsLoading(true);
      DesignerAPI.AutoComplete({ key: searchText })
        .then((v) => v.json())
        .then((v) => API.toLang<Designer[]>(v, i18n.language))
                  .then((v) => {
            console.log('Designers loaded:', v);
            setDesigners(v || []);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error loading designers:', error);
          setDesigners([]);
          setIsLoading(false);
        });
    } else {
      setDesigners([]);
    }
  }, [searchText, i18n.language]);

  // Обработка выбора автора
      const handleAuthorSelect = useCallback((designer: Designer) => {
      console.log('Author selected:', designer);
      const newAuthor = designer as AuthorT;
      console.log('New author:', newAuthor);
      onChange([newAuthor]); // Заменяем автора (может быть только один)
    setSearchText('');
    setShowDropdown(false);
  }, [onChange]);

  // Обработка удаления автора
  const handleAuthorRemove = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Обработка фокуса на инпуте


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = document.querySelector('[data-author-field]');
      if (container && !container.contains(target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (value && value.length > 0) {
    const author = value[0];
    const cv = computeDesigner(author, { showFullName: true });
    return (
      <FieldContainer>
        <AuthorDisplay>
          <AuthorChip>
            <FlexBox width={24} height={24} style={{ borderRadius: '3px' }}>
              <Avatar {...cv} rounded />
            </FlexBox>
            <span>{cv.fullName}</span>
            <RemoveButton onClick={handleAuthorRemove}>
              ×
            </RemoveButton>
          </AuthorChip>
        </AuthorDisplay>
      </FieldContainer>
    );
  }

  return (
    <FieldContainer>
      <div style={{ position: 'relative' }} data-author-field>
        <SearchInputComponent
          searchText={searchText}
          setSearchText={(value) => {
            setSearchText(value);
            if (value.length >= 2) {
              setShowDropdown(true);
            } else {
              setShowDropdown(false);
            }
          }}
          placeholder={placeholder || t('search_by_name')}
        />
        {showDropdown && (searchText.length >= 2) && (
          <DropdownContainer>
            {isLoading ? (
              <DropdownItem>
                <AuthorName>{t('text.loading')}</AuthorName>
              </DropdownItem>
            ) : designers.length > 0 ? (
              designers.map((designer) => {
                const cv = computeDesigner(designer, { showFullName: true });
                return (
                  <DropdownItem
                    key={designer.accountId}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAuthorSelect(designer);
                    }}
                  >
                    <FlexBox align="center" gx={8}>
                      <FlexBox width={32} height={32} style={{ borderRadius: '3px' }}>
                        <Avatar {...cv} rounded />
                      </FlexBox>
                      <AuthorName 
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAuthorSelect(designer);
                        }}
                      >
                        {cv.fullName}
                      </AuthorName>
                    </FlexBox>
                  </DropdownItem>
                );
              })
            ) : (
              <DropdownItem>
                <AuthorName>{t('text.notfound')}</AuthorName>
              </DropdownItem>
            )}
          </DropdownContainer>
        )}
      </div>
    </FieldContainer>
  );
};

export default AuthorField;
import React, { FC, useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Avatar, Colors, FlexBox } from '@brg/ui';
import { Designer, DesignerAPI, API } from '@brg/api';
import { computeDesigner } from 'shared/helpers/computeData';
import { SearchInputComponent } from 'shared/components/SearchInput/SearchInputComponent';
import { useTheme } from 'styled-components';

export type AuthorT = Designer<'Local'> & { disabled?: boolean };

const FieldContainer = styled(FlexBox)`
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.label};
  font-weight: 500;
`;



const DropdownContainer = styled.div`
  position: absolute;
  z-index: 1000;
  top: 100%;
  background-color: ${({ theme }) => theme.projectEdit.inputs.bg};
  max-height: 200px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 6px 6px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
  margin-bottom: 50px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.mobileSearch.scroll.background};
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.mobileSearch.scroll.body};
    border-radius: 2px;
    width: 4px;
  }
`;

const DropdownItem = styled(FlexBox)`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid ${Colors.galBlack10};
  
  &:hover {
    background-color: ${Colors.galBlack5};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const AuthorName = styled.span`
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.projectEdit.inputs.color};
`;

const AuthorDisplay = styled(FlexBox)`
  width: 100%;
  min-height: 48px;
  padding: 8px 12px;
  border-radius: 4px;
  align-items: center;
  gap: 8px;
`;

const AuthorChip = styled(FlexBox)`
  background-color: ${Colors.galBlack5};
  border: 1px solid ${Colors.galBlack21};
  border-radius: 4px;
  padding: 4px 8px;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.projectEdit.inputs.color};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  color: ${Colors.galBlack47};
  font-size: 12px;
  
  &:hover {
    background-color: ${Colors.galBlack10};
  }
`;

interface AuthorFieldProps {
  value: AuthorT[];
  onChange: (authors: AuthorT[]) => void;
  required?: boolean;
  placeholder?: string;
}

const AuthorField: FC<AuthorFieldProps> = ({
  value,
  onChange,
  required = false,
  placeholder
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
  const [searchText, setSearchText] = useState('');
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Поиск дизайнеров
      useEffect(() => {
      if (searchText.length >= 2) {
        console.log('Searching for designers with:', searchText);
        setIsLoading(true);
      DesignerAPI.AutoComplete({ key: searchText })
        .then((v) => v.json())
        .then((v) => API.toLang<Designer[]>(v, i18n.language))
                  .then((v) => {
            console.log('Designers loaded:', v);
            setDesigners(v || []);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error loading designers:', error);
          setDesigners([]);
          setIsLoading(false);
        });
    } else {
      setDesigners([]);
    }
  }, [searchText, i18n.language]);

  // Обработка выбора автора
      const handleAuthorSelect = useCallback((designer: Designer) => {
      console.log('Author selected:', designer);
      const newAuthor = designer as AuthorT;
      console.log('New author:', newAuthor);
      onChange([newAuthor]); // Заменяем автора (может быть только один)
    setSearchText('');
    setShowDropdown(false);
  }, [onChange]);

  // Обработка удаления автора
  const handleAuthorRemove = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Обработка фокуса на инпуте


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = document.querySelector('[data-author-field]');
      if (container && !container.contains(target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (value && value.length > 0) {
    const author = value[0];
    const cv = computeDesigner(author, { showFullName: true });
    return (
      <FieldContainer>
        <AuthorDisplay>
          <AuthorChip>
            <FlexBox width={24} height={24} style={{ borderRadius: '3px' }}>
              <Avatar {...cv} rounded />
            </FlexBox>
            <span>{cv.fullName}</span>
            <RemoveButton onClick={handleAuthorRemove}>
              ×
            </RemoveButton>
          </AuthorChip>
        </AuthorDisplay>
      </FieldContainer>
    );
  }

  return (
    <FieldContainer>
      <div style={{ position: 'relative' }} data-author-field>
        <SearchInputComponent
          searchText={searchText}
          setSearchText={(value) => {
            setSearchText(value);
            if (value.length >= 2) {
              setShowDropdown(true);
            } else {
              setShowDropdown(false);
            }
          }}
          placeholder={placeholder || t('search_by_name')}
        />
        {showDropdown && (searchText.length >= 2) && (
          <DropdownContainer>
            {isLoading ? (
              <DropdownItem>
                <AuthorName>{t('text.loading')}</AuthorName>
              </DropdownItem>
            ) : designers.length > 0 ? (
              designers.map((designer) => {
                const cv = computeDesigner(designer, { showFullName: true });
                return (
                  <DropdownItem
                    key={designer.accountId}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAuthorSelect(designer);
                    }}
                  >
                    <FlexBox align="center" gx={8}>
                      <FlexBox width={32} height={32} style={{ borderRadius: '3px' }}>
                        <Avatar {...cv} rounded />
                      </FlexBox>
                      <AuthorName 
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAuthorSelect(designer);
                        }}
                      >
                        {cv.fullName}
                      </AuthorName>
                    </FlexBox>
                  </DropdownItem>
                );
              })
            ) : (
              <DropdownItem>
                <AuthorName>{t('text.notfound')}</AuthorName>
              </DropdownItem>
            )}
          </DropdownContainer>
        )}
      </div>
    </FieldContainer>
  );
};

export default AuthorField;
